import type {RGBA} from '@Utils/color.util';
import type {QRCodeItem, QRCodeItemObject} from '@PosterWhiteboard/items/qr-code-item.class';
import type {VideoItem, VideoItemObject} from '@PosterWhiteboard/items/video-item/video-item.class';
import type {FancyTextItem, FancyTextItemObject} from '@PosterWhiteboard/items/fancy-text-item/fancy-text-item.class';
import type {ImageItem, ImageItemObject} from '@PosterWhiteboard/items/image-item/image-item.class';
import type {TextItem} from '@PosterWhiteboard/items/text-item/text-item.class';
import type {VectorItem} from '@PosterWhiteboard/items/vector-item/vector-item.class';
import type {TextSlideItem, TextSlideItemObject} from '@PosterWhiteboard/items/slideshow-item/slide-items/text-slide-item.class';
import type {ImageSlideItem, ImageSlideItemObject} from '@PosterWhiteboard/items/slideshow-item/slide-items/image-slide-item.class';
import type {VideoSlideItem, VideoSlideItemObject} from '@PosterWhiteboard/items/slideshow-item/slide-items/video-slide-item.class';
import type {VectorItemObject} from '@PosterWhiteboard/items/vector-item/vector-item.types';
import type {TextItemObject} from '@PosterWhiteboard/items/text-item/text-item.types';
import type {StickerItem, StickerItemObject} from '@PosterWhiteboard/items/sticker-item.class';
import type {SlideshowItem, SlideshowItemObject} from '@PosterWhiteboard/items/slideshow-item/slideshow-item.class';
import type {TableItem, TableItemObject} from '@PosterWhiteboard/items/table-item/table-item.class';
import type {MenuItem, MenuItemObject} from '@PosterWhiteboard/items/menu-item/menu-item.class';
import type {SeparatorType, TabsItem, TabsItemObject} from '@PosterWhiteboard/items/tabs-item/tabs-item.class';
import type {ImageBackground, ImageBackgroundObject} from '@PosterWhiteboard/page/background/image-background.class';
import type * as Fabric from '@postermywall/fabricjs-2';
import type {SerializedShadowOptions, TFiller} from '@postermywall/fabricjs-2';
import type {TranscriptItem} from '@PosterWhiteboard/items/transcript-item/transcript-item';
import type {TranscriptItemObject} from '@PosterWhiteboard/items/transcript-item/transcript-item.types';
import type {RectangleItem, RectangleItemObject} from '@PosterWhiteboard/items/rectangle-item/rectangle-item';
import type {LineItem, LineItemObject} from '@PosterWhiteboard/items/line-item/line-item';
import type {ItemAnimationObject} from '@PosterWhiteboard/animation/animation.class';
import type {ItemEffectsObject} from '@PosterWhiteboard/classes/item-effects.class';
import type {ItemBorderObject} from '@PosterWhiteboard/classes/item-border.class';
import type {TextStylesObject, TextVerticalAlignType} from '@PosterWhiteboard/classes/text-styles.class';
import type {TextListObject} from '@PosterWhiteboard/items/text-item/text-list';
import type {MorphType} from '@Libraries/fancy-text-library';
import type {FillObject} from '@PosterWhiteboard/classes/fill.class';
import type {TransitionObject} from '@PosterWhiteboard/models/transition.class';
import type {LayoutTypes} from '@PosterWhiteboard/items/layouts/layout.types';
import type {MediaSlideObject} from '@PosterWhiteboard/items/slideshow-item/media-slide.class';
import type {SubtitleTemplateType} from '@PosterWhiteboard/items/transcript-item/subtitle/template-styles.types';
import type {DeepPartial} from '@/global';
import type {TMat2D} from '@postermywall/fabricjs-2/src/typedefs';
import type {ColorStop, GradientCoords, GradientType, GradientUnits} from '@postermywall/fabricjs-2/src/gradient/typedefs';
import type {AnimationObject} from '@PosterWhiteboard/classes/item-animation.class';
import type {LineCapStyle, LineDashType} from '@PosterWhiteboard/items/line-item/line-item.types';

export type ItemType =
  | StickerItem
  | ImageItem
  | QRCodeItem
  | VideoItem
  | FancyTextItem
  | TextItem
  | VectorItem
  | SlideshowItem
  | TextSlideItem
  | ImageSlideItem
  | VideoSlideItem
  | TableItem
  | MenuItem
  | TabsItem
  | ImageBackground
  | RectangleItem
  | LineItem
  | TranscriptItem;

export type ItemObject =
  | ImageItemObject
  | ImageBackgroundObject
  | StickerItemObject
  | VideoItemObject
  | QRCodeItemObject
  | VectorItemObject
  | TextItemObject
  | FancyTextItemObject
  | TableItemObject
  | MenuItemObject
  | TabsItemObject
  | SlideshowItemObject
  | RectangleItemObject
  | LineItemObject
  | TranscriptItemObject;

export interface FabricGradientObject<T extends GradientType> {
  type: T;
  gradientUnits: GradientUnits;
  colorStops: ColorStop[];
  coords: Partial<GradientCoords<T>>;
  gradientTransform: TMat2D;
  offsetX: number;
  offsetY: number;
}

export type TFabricColor = string | TFiller | null;
export type TSerializedFabricColor = string | null | FabricGradientObject<'radial'> | FabricGradientObject<'linear'>;

export type ItemsWithTextStyles = TextSlideItem | TextItem | TabsItem | TableItem;

export type SlideItemObject = TextSlideItemObject | ImageSlideItemObject | VideoSlideItemObject;

export type ItemsWithFonts = TextItem | TabsItem | TableItem | MenuItem | SlideshowItem | TranscriptItem;

export const RESELECT_ITEM_EVENT_TYPE = 'reselectItem';

export enum ITEM_TYPE {
  IMAGE = 'image',
  FANCY_TEXT = 'fancytext',
  QR_CODE = 'qrcode',
  RECTANGLE = 'rectangle',
  LINE = 'line',
  VIDEO = 'video',
  STICKER = 'sticker',
  SLIDESHOW = 'slideshow',
  TEXT = 'text',
  VECTOR = 'vector',
  TEXTSLIDE = 'textslide',
  IMAGESLIDE = 'imageslide',
  VIDEOSLIDE = 'videoslide',
  DRAWING = 'drawing',
  TAB = 'tabs',
  TABLE = 'table',
  MENU = 'menu',
  IMAGEBACKGROUND = 'imagebackground',
  VIDEOBACKGROUND = 'videobackground',
  TRANSCRIPT = 'transcript',
}

export enum RemoveBackgroundStatus {
  NONE = 'none',
  REMOVING = 'removing',
  REMOVED = 'removed',
}

export interface FabricLoadingObjects {
  group: Fabric.Group;
  text: Fabric.Text;
  background: Fabric.Rect;
  circularLoaderProgress: Fabric.Circle;
  circularLoaderBackground: Fabric.Circle;
}

export interface InitItemOpts {
  width?: number;
  height?: number;
  scaleX?: number;
  scaleY?: number;
  shadow?: SerializedShadowOptions;
}

export interface ShowItemLoadingOtps {
  /**
   * Array of strings to show for item loading starting from longest to shortest version.
   */
  langTextKeys?: string[];
  progress?: ItemLoadingProgress;
}

export type ItemLoadingProgress = ItemLoadingFakeProgress;

export interface ItemLoadingFakeProgress {
  type: ItemLoadingProgressType.FAKE;
  /**
   * Approximate time in Seconds that the fake loading should take. The loader will hit 85% at this time and slow down drastically after that
   */
  expectedTotalTime: number;
}

export enum ItemLoadingProgressType {
  FAKE = 'fake',
}

export interface ItemConfig {
  x?: number;
  y?: number;
  alpha?: number;
  width?: number;
  height?: number;
  rotation?: number;
  visible?: boolean;
  erasable?: boolean;
  glow?: number;
  dropShadow?: number;
  dropShadowColor?: RGBA;
  dropShadowAlpha?: number;
  dropShadowAngle?: number;
  solidBorderType?: number;
  solidBorderColor?: RGBA;
  solidBorderThickness?: number;
  scaleX?: number;
  scaleY?: number;
  flipX?: boolean;
  flipY?: boolean;
  clickableLink?: string;
  lockMovement?: boolean;
  fillType?: number;
  gradientFillColor1?: RGBA;
  gradientFillColor2?: RGBA;
  patternFill?: number;
  version?: number;
  isNew?: boolean;
}

export interface BaseItemObject {
  uid: string;
  gitype: ITEM_TYPE;
  fill: TSerializedFabricColor;
  idOriginalOwner?: number;
  sourcePosterHashId: string;
  left: number;
  top: number;
  alpha: number;
  width: number;
  height: number;
  angle: number;
  erasable: boolean | 'deep';
  clickableLink: string;
  border: ItemBorderObject;
  introAnimation: ItemAnimationObject;
  outroAnimation: ItemAnimationObject;
  onPageStartTime: number;
  onPageEndTime: number;
  shadow: SerializedShadowOptions | null;
  scaleX: number;
  scaleY: number;
  flipX: boolean;
  flipY: boolean;
  lockMovementX: boolean;
  lockMovementY: boolean;
  lockRotation: boolean;
  lockScalingX: boolean;
  lockScalingY: boolean;
  version: number;
  groupId: string | null;
}

export const BLUR_FOR_LIGHT_GLOW = 8;
export const BLUR_FOR_STRONG_GLOW = 10;

export interface AnimationFrameStateObject {
  left: number;
  top: number;
  alpha: number;
  angle: number;
  scaleX: number;
  scaleY: number;
}

export const SHADOW_REFERENCE_DIMENSION = 500;

export interface CommonItemStyles {
  shadow: SerializedShadowOptions;
  border?: ItemBorderObject;
  flipX?: boolean;
  flipY?: boolean;
  opacity?: number;
  scaleX?: number;
  scaleY?: number;
  introAnimation?: AnimationObject;
  outroAnimation?: AnimationObject;
}

export interface CopyableItemStyles extends CommonItemStyles {
  activeWordTextStyles?: TextStylesObject;
  animationStyle?: SubtitleTemplateType;
  colorsFancyText?: RGBA[];
  backFill?: FillObject;
  effects?: DeepPartial<ItemEffectsObject>;
  fontFamily?: string;
  fontFamily2?: string;
  frontFill?: TSerializedFabricColor;
  highlightedBackgroundColor?: RGBA;
  highlightedTextColor?: RGBA;
  iconsColor?: RGBA;
  iconsSize?: number;
  idFancyText?: string;
  isBold2?: boolean;
  isItalic2?: boolean;
  layoutStyle?: LayoutTypes;
  lineThrough2?: boolean;
  list?: TextListObject;
  mediaSlide?: MediaSlideObject;
  morphAmount?: number;
  morphType?: MorphType;
  rx?: number;
  ry?: number;
  separatorColor?: RGBA;
  separatorType?: SeparatorType;
  slideDuration?: number;
  textBorder?: ItemBorderObject;
  textStyles?: DeepPartial<TextStylesObject>;
  transition?: TransitionObject;
  underLine2?: boolean;
  verticalAlign?: TextVerticalAlignType;
  verticalPadding?: number;
  xSpacing?: number;
  ySpacing?: number;
  lineDashType?: LineDashType;
  lineCapStart?: LineCapStyle;
  lineCapEnd?: LineCapStyle;
  customGapSize?: number;
  customDashSize?: number;
  isRounded?: boolean;
}

export enum AnimationType {
  NONE = 'none',
  FADE = 'fade',
  BLOCK = 'block',
  SLIDE = 'slide',
  RISE = 'rise',
  PAN = 'pan',
  POP = 'pop',
  TUMBLE = 'tumble',
  JELLO = 'jello',
  ROTATE = 'rotate',
  ZOOM_IN = 'zoom_in',
  ZOOM_OUT = 'zoom_out',
  BOUNCE_IN_DOWN = 'bounce_in_down',
  BOUNCE = 'bounce',
  WIPE = 'wipe',
  WIPE_DOWN = 'wipe_down',
  WIPE_UP = 'wipe_up',
  WIPE_LEFT = 'wipe_left',
  WIPE_RIGHT = 'wipe_right',
  PEEK = 'peek',
  PEEK_DOWN = 'peek_down',
  PEEK_UP = 'peek_up',
  PEEK_LEFT = 'peek_left',
  PEEK_RIGHT = 'peek_right',
  SHRINK = 'shrink',
  EXPAND = 'expand',
  PAN_LEFT = 'pan_left',
  PAN_RIGHT = 'pan_right',
  PAN_UP = 'pan_up',
  PAN_DOWN = 'pan_down',
  ZOOM = 'zoom',
  SCALE = 'scale',
}
